// lendcord-core's PeopleDirectoryController (/enterprise/people) —
// ENTERPRISE-only server-side. The list endpoint is server-paginated
// (page/size/sort query params), unlike members.api.ts's flat-array
// listRoleUpgradeRequests — baseApi's ApiRequestArgs has no `params` field
// (see baseApi.ts), so the query string is built into the URL directly here
// rather than passed as a separate object.

import { baseApi } from "@/app/store/baseApi";
import type { PageResponse, PeopleDirectoryCounts, PeopleRoleFilter, PeopleStatusFilter, PersonDetail, PersonListItem } from "../people.types";

export interface ListPeopleParams {
  role?: PeopleRoleFilter;
  status?: PeopleStatusFilter;
  page?: number;
  size?: number;
}

function buildListPeopleUrl({ role, status, page, size }: ListPeopleParams): string {
  const params = new URLSearchParams();
  if (role) params.set("role", role);
  if (status) params.set("status", status);
  if (page !== undefined) params.set("page", String(page));
  if (size !== undefined) params.set("size", String(size));
  const query = params.toString();
  return query ? `/enterprise/people?${query}` : "/enterprise/people";
}

export const peopleApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    listPeople: builder.query<PageResponse<PersonListItem>, ListPeopleParams>({
      query: (params) => ({ url: buildListPeopleUrl(params), auth: true }),
      providesTags: ["PeopleDirectory"],
    }),
    getPeopleDirectoryCounts: builder.query<PeopleDirectoryCounts, void>({
      query: () => ({ url: "/enterprise/people/counts", auth: true }),
      providesTags: ["PeopleDirectory"],
    }),
    // Read-only "view application" — no invalidation needed, nothing on this
    // page ever mutates the underlying application.
    getPersonDetail: builder.query<PersonDetail, string>({
      query: (applicationId) => ({ url: `/enterprise/people/${applicationId}`, auth: true }),
    }),
  }),
});

export const { useListPeopleQuery, useGetPeopleDirectoryCountsQuery, useGetPersonDetailQuery } = peopleApi;
